import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, Loader2, AlertTriangle, Mail, HelpCircle, ChevronRight } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { useTerms } from "../../lib/BrandContext";
import { useSmartBack } from "../../lib/backNavigation";
import { MVP_INDUSTRY_LOCKED_TO_SPORTS } from "../../lib/featureFlags";
import { planDisplayName } from "../../components/trialBannerVisibility";
import { toast } from "sonner";

const PLAN_NAMES = { trial: "Free Trial", starter: "Starter", pro: "Pro", organization: "Organization", enterprise: "Enterprise" };
const PLAN_RANK = { trial: 0, starter: 1, pro: 2, organization: 3, enterprise: 4 };

export default function Settings() {
  const { user } = useAuth();
  const { terms } = useTerms();
  // Contextual Back — only rendered when Settings was reached from a
  // specific team page (Team → Settings tab). Bottom-nav entry has no
  // `?from=` so this is null-safe and the affordance is hidden.
  const back = useSmartBack(null, null);
  const [org, setOrg] = useState(null);
  const [name, setName] = useState("");
  const [plan, setPlan] = useState(null);
  const [brand, setBrand] = useState(null);
  const [selecting, setSelecting] = useState("");
  const [savingOrg, setSavingOrg] = useState(false);
  const [downgrade, setDowngrade] = useState(null); // {planId, planName, warnings:[], suggestedPlan}

  const loadPlan = () => api.get("/orgs/mine/plan").then((r) => setPlan(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/orgs/mine")
      .then((r) => { setOrg(r.data); setName(r.data.name); })
      .catch(() => { setOrg({ name: "", plan: "trial" }); });
    api.get("/brand").then((r) => setBrand(r.data)).catch(() => setBrand({ plans: [] }));
    loadPlan();
  }, []);

  // Jump to #plan anchor if the URL includes it
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#plan") {
      setTimeout(() => {
        const el = document.getElementById("plan");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, []);

  const save = async () => {
    setSavingOrg(true);
    try {
      await api.patch("/orgs/mine", { name });
      toast.success("Settings saved successfully");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSavingOrg(false);
    }
  };

  /** Change the org's Industry Profile. Fires a 'terms:changed' event so
   *  the whole app re-renders in the new vocabulary without a full reload. */
  const changeIndustryProfile = async (industryId) => {
    try {
      const { data } = await api.patch("/orgs/mine", { industry_profile: industryId });
      setOrg((prev) => ({ ...(prev || {}), ...data }));
      window.dispatchEvent(new Event("terms:changed"));
      toast.success("Industry profile updated");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      toast.error(msg || "Could not update industry profile");
    }
  };

  /** Run the actual plan switch — used by both direct-pick and the confirm dialog. */
  const performSwitch = async (planId) => {
    setSelecting(planId);
    try {
      await api.post("/orgs/mine/plan", { plan: planId });
      toast.success(`Plan changed to ${PLAN_NAMES[planId]}`);
      loadPlan();
    } catch (e) {
      // While BILLING_ENABLED=false the backend returns 503 with a
      // billing_not_ready payload. Surface the friendly copy without
      // silently switching the plan.
      if (e?.response?.status === 503 && e.response.data?.detail?.billing_not_ready) {
        toast.info(e.response.data.detail.message || "Paid plans aren't available yet.");
      } else {
        const msg = formatApiErrorDetail(e.response?.data?.detail);
        toast.error(msg || "Could not change plan");
      }
    } finally {
      setSelecting("");
    }
  };

  /** Compute a friendly capacity warning if the target plan can't fit current usage. */
  const computeDowngradeWarning = (planId) => {
    if (!plan || !brand?.plans) return null;
    const target = brand.plans.find((p) => p.id === planId);
    if (!target) return null;
    const usage = plan.usage || {};
    const warnings = [];
    if (target.max_athletes != null && (usage.active_athletes || 0) > target.max_athletes) {
      warnings.push(
        `${target.name} allows up to ${target.max_athletes} active ${terms.athletes}. You currently have ${usage.active_athletes}.`
      );
    }
    if (target.max_teams != null && (usage.active_teams || 0) > target.max_teams) {
      warnings.push(
        `${target.name} allows ${target.max_teams} ${target.max_teams === 1 ? terms.team : terms.teams}. You currently have ${usage.active_teams}.`
      );
    }
    if (warnings.length === 0) return null;
    // Pick a suggested plan that fits — must be different from the target
    const targetRank = PLAN_RANK[planId] ?? 0;
    const suggested = brand.plans.find(
      (p) =>
        p.id !== planId &&
        p.id !== "trial" &&
        PLAN_RANK[p.id] > targetRank &&
        (p.max_athletes == null || (usage.active_athletes || 0) <= p.max_athletes) &&
        (p.max_teams == null || (usage.active_teams || 0) <= p.max_teams)
    );
    return { planId, planName: target.name, warnings, suggested };
  };

  const choosePlan = async (planId) => {
    if (planId === "enterprise") {
      toast.info("Please contact sales to activate Enterprise.");
      return;
    }
    const warning = computeDowngradeWarning(planId);
    if (warning) {
      setDowngrade(warning);
      return;
    }
    performSwitch(planId);
  };

  const plans = (brand?.plans || []).filter((p) => p.id !== "trial");
  const currentPlan = plan?.plan || org?.plan || "trial";
  const earlyAccess = !!(plan?.early_access || (brand?.free_early_access && currentPlan === "trial" && !plan?.beta_access));

  return (
    <AppLayout>
      {/* Contextual Back — visible only when Settings was reached from a
          specific team page. Bottom-nav entry omits `?from=` so nothing
          shows here and Settings stays the global page. */}
      {back.isContextual ? (
        <Link
          to={back.to}
          className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-ink-500 hover:text-ink-900"
          data-testid="settings-back-context"
        >
          <ChevronRight className="h-4 w-4 rotate-180" /> Back to {back.label}
        </Link>
      ) : null}
      <div>
        <p className="label-caps">Settings</p>
        <h1 className="h2">Plan &amp; Billing</h1>
        <p className="text-sm text-ink-500">Manage your {terms.team} name and subscription.</p>
      </div>

      {/* Team or Business Name */}
      <div className="mt-6 max-w-xl rounded-xl border border-ink-100 bg-white p-6 shadow-sm">
        <p className="label-caps">{terms.Team} or Business Name</p>
        <Label className="mt-2 block">Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-12" data-testid="settings-org-name" />
        <p className="mt-1 text-xs text-ink-500">Shown to parents on invitations and receipts.</p>
        <div className="mt-4">
          <Button
            onClick={save}
            disabled={savingOrg}
            className="h-12 bg-brand font-semibold text-white hover:bg-brand-hover"
            data-testid="settings-save-button"
          >
            {savingOrg ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Saving…
              </span>
            ) : (
              "Save changes"
            )}
          </Button>
        </div>
      </div>

      {/* Industry Profile — controls terminology across the app. */}
      <div
        className={`mt-6 max-w-xl rounded-xl border border-ink-100 bg-white p-6 shadow-sm ${MVP_INDUSTRY_LOCKED_TO_SPORTS ? "hidden" : ""}`}
        data-testid="settings-industry-profile"
      >
        <p className="label-caps">Industry Profile</p>
        <p className="mt-1 text-sm text-ink-600">
          Determines the vocabulary shown throughout Squad Fee ({terms.leader.toLowerCase()},{" "}
          {terms.group_lower}, {terms.participant_lower}, etc.). Change anytime — no data is affected.
        </p>
        <div className="mt-4 grid gap-2">
          {(brand?.industry_profiles || []).map((p) => {
            const active = org?.industry_profile === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => changeIndustryProfile(p.id)}
                className={`flex items-start gap-3 rounded-lg border p-3 text-left transition ${
                  active
                    ? "border-brand bg-brand-50 ring-1 ring-brand/20"
                    : "border-ink-200 bg-white hover:bg-ink-100"
                }`}
                data-testid={`settings-industry-${p.id}`}
              >
                <span
                  className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                    active ? "border-brand bg-brand" : "border-ink-300"
                  }`}
                  aria-hidden="true"
                >
                  {active ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-ink-900">{p.name}</p>
                  <p className="text-xs text-ink-500">{p.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Help & support */}
      <div className="mt-6 max-w-xl rounded-xl border border-ink-100 bg-white p-6 shadow-sm" data-testid="settings-help-section">
        <p className="label-caps">Need a hand?</p>
        <h2 className="mt-2 font-heading text-lg font-bold text-ink-900">Help &amp; support</h2>
        <p className="mt-1 text-sm text-ink-500">
          We&apos;re a small team and we&apos;ll always reply within a business day.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <a
            href={`mailto:${brand?.support_email || "support@squadfee.com"}?subject=${encodeURIComponent(`${brand?.app_name || "Squad Fee"} — help request`)}`}
            className="inline-flex h-11 items-center rounded-lg border border-brand bg-white px-4 text-sm font-semibold text-brand hover:bg-brand-50"
            data-testid="settings-support-email"
          >
            <Mail className="mr-2 h-4 w-4" /> Email support
          </a>
          <Link
            to="/faq"
            className="inline-flex h-11 items-center rounded-lg border border-ink-300 bg-white px-4 text-sm font-semibold text-ink-700 hover:bg-ink-100"
            data-testid="settings-faq-link"
          >
            <HelpCircle className="mr-2 h-4 w-4" /> Read the FAQ
          </Link>
        </div>
      </div>

      {/* Plan */}
      <div id="plan" className="mt-8 scroll-mt-20">
        <h2 className="h3">Your plan</h2>
        {plan ? (
          <div className="mt-3 rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-heading text-xl font-bold text-ink-900" data-testid="plan-summary-name">{planDisplayName(plan, PLAN_NAMES)}</p>
                {plan.beta_access ? (
                  <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-brand/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-brand" data-testid="plan-summary-beta-access">
                    <Sparkles className="h-3 w-3" /> Private Beta Access
                  </p>
                ) : plan.early_access ? (
                  <p className="text-sm text-ink-500" data-testid="plan-summary-early-access">
                    Free during Early Access. No credit card required. Using {plan.usage?.active_athletes || 0} / {plan.limits?.athletes} active {terms.athletes} · {plan.usage?.active_teams || 0} / {plan.limits?.teams} {terms.teams}.
                  </p>
                ) : plan.plan === "trial" ? (
                  plan.trial_expired ? (
                    <p className="text-sm font-semibold text-rose-600">Trial ended — choose a plan below to continue.</p>
                  ) : (
                    <p className="text-sm text-ink-500">
                      {plan.trial_days_remaining} {plan.trial_days_remaining === 1 ? "day" : "days"} remaining on your free trial.
                    </p>
                  )
                ) : (() => {
                  const activeAthletes = plan.usage?.active_athletes || 0;
                  const activeTeams = plan.usage?.active_teams || 0;
                  const limitAthletes = plan.limits?.athletes;
                  const limitTeams = plan.limits?.teams;
                  const athletesOver = limitAthletes != null && activeAthletes > limitAthletes;
                  const teamsOver = limitTeams != null && activeTeams > limitTeams;
                  if (athletesOver || teamsOver) {
                    const planName = PLAN_NAMES[plan.plan] || plan.plan;
                    return (
                      <>
                        <p className="text-sm font-semibold text-rose-700" data-testid="plan-summary-over-limit">
                          {teamsOver ? `${activeTeams} · over limit on ${terms.teams}` : null}
                          {teamsOver && athletesOver ? " · " : null}
                          {athletesOver ? `${activeAthletes} · over limit on ${terms.athletes}` : null}
                        </p>
                        <p className="mt-1 text-sm text-ink-500">
                          You&apos;re over the {planName} limit. Upgrade to add more active {terms.teams} or {terms.athletes}.
                        </p>
                      </>
                    );
                  }
                  return (
                    <p className="text-sm text-ink-500" data-testid="plan-summary-usage">
                      Using {activeAthletes}
                      {limitAthletes ? ` / ${limitAthletes}` : ""} active {terms.athletes} ·
                      {" "}{activeTeams}
                      {limitTeams ? ` / ${limitTeams}` : ""} {terms.teams}
                    </p>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : null}

        {earlyAccess ? (
          <p className="mt-6 text-sm text-ink-700" data-testid="settings-planned-pricing-note">
            <span className="font-semibold">Planned pricing after Early Access.</span> These plans aren&apos;t available yet. We&apos;ll notify you before paid subscriptions begin — you&apos;ll never be charged automatically.
          </p>
        ) : null}
        <div className="mt-5 grid gap-6 md:grid-cols-2">
          {plans.map((p) => {
            const isCurrent = p.id === currentPlan;
            const isEnterprise = p.id === "enterprise";
            const supportEmail = brand?.support_email || "support@squadfee.com";
            return (
              <div
                key={p.id}
                data-testid={`settings-plan-${p.id}`}
                className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm transition-all ${
                  isCurrent
                    ? "border-emerald-400 ring-2 ring-emerald-200"
                    : p.popular
                    ? "border-brand ring-2 ring-brand/20"
                    : "border-ink-100"
                }`}
              >
                {isCurrent ? (
                  <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-sm">
                    <Check className="h-3 w-3" /> Current plan
                  </span>
                ) : p.popular ? (
                  <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                    <Sparkles className="h-3 w-3" /> Most popular
                  </span>
                ) : null}
                <h3 className="font-heading text-xl font-bold text-ink-900">{p.name}</h3>
                <p className="mt-1 text-xs text-ink-500">{p.best_for}</p>
                <div className="mt-3 flex items-baseline gap-1">
                  {isEnterprise ? (
                    <span className="font-heading text-3xl font-bold text-ink-900">{p.price_label}</span>
                  ) : (
                    <>
                      <span className="font-heading text-4xl font-bold text-ink-900">{p.price_label}</span>
                      <span className="text-sm font-semibold text-ink-500">{p.period}</span>
                    </>
                  )}
                </div>
                <p className="mt-2 text-xs font-bold uppercase tracking-wide text-ink-500">
                  {p.max_teams == null
                    ? "Custom limits"
                    : `Up to ${p.max_teams} ${p.max_teams > 1 ? terms.teams : terms.team} and ${p.max_athletes} active ${terms.athletes}`}
                </p>
                <ul className="mt-5 flex-1 space-y-2 text-sm text-ink-700">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {f}
                    </li>
                  ))}
                </ul>
                {/* Enterprise is contact-only — render a mailto: link instead of
                    the standard "choose plan" button so the coach can't trigger
                    a self-serve switch into a custom-priced tier. */}
                {earlyAccess && !isEnterprise && !isCurrent ? (
                  <p
                    data-testid={`settings-plan-cta-${p.id}`}
                    className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-md border border-dashed border-ink-200 bg-ink-50 text-sm font-semibold text-ink-500"
                  >
                    Available after Early Access
                  </p>
                ) : isEnterprise ? (
                  <a
                    href={`mailto:${supportEmail}?subject=Squad Fee Enterprise`}
                    data-testid={`settings-plan-cta-${p.id}`}
                    className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-md border border-ink-300 bg-white font-semibold text-ink-900 hover:bg-ink-100"
                  >
                    Contact Us
                  </a>
                ) : (
                  <Button
                    onClick={() => choosePlan(p.id)}
                    disabled={isCurrent || !!selecting}
                    className={`mt-6 h-12 w-full font-semibold ${
                      isCurrent
                        ? "bg-ink-100 text-ink-500 cursor-default hover:bg-ink-100"
                        : p.popular
                        ? "bg-brand text-white hover:bg-brand-hover"
                        : "border border-ink-300 bg-white text-ink-900 hover:bg-ink-100"
                    }`}
                    data-testid={`settings-plan-cta-${p.id}`}
                  >
                    {selecting === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? "Current plan" : p.cta}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-xs text-ink-500">
          {earlyAccess ? "Your data is always preserved." : "You can change plans anytime. Your data is always preserved."}
        </p>
      </div>

      {/* Downgrade warning — soft confirmation before switching to a smaller plan */}
      <AlertDialog open={!!downgrade} onOpenChange={(o) => { if (!o) setDowngrade(null); }}>
        <AlertDialogContent data-testid="downgrade-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <AlertTriangle className="h-4 w-4" />
              </span>
              Heads-up before switching to {downgrade?.planName}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-ink-700">
                <ul className="list-disc space-y-1 pl-5" data-testid="downgrade-warning-list">
                  {downgrade?.warnings?.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
                <p>
                  To switch to <span className="font-semibold">{downgrade?.planName}</span>, archive
                  some {terms.athletes} or {terms.teams} first
                  {downgrade?.suggested ? (
                    <>
                      , or pick{" "}
                      <span className="font-semibold">{downgrade.suggested.name}</span> instead — it
                      fits your current usage.
                    </>
                  ) : (
                    "."
                  )}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <AlertDialogCancel
              data-testid="downgrade-cancel-button"
              className="h-11 sm:order-1"
            >
              Cancel
            </AlertDialogCancel>
            {downgrade?.suggested ? (
              <Button
                type="button"
                onClick={() => {
                  const target = downgrade.suggested.id;
                  setDowngrade(null);
                  performSwitch(target);
                }}
                className="h-11 bg-brand font-semibold text-white hover:bg-brand-hover sm:order-2"
                data-testid="downgrade-pick-suggested-button"
              >
                Switch to {downgrade.suggested.name} instead
              </Button>
            ) : null}
            <AlertDialogAction
              onClick={() => {
                const target = downgrade.planId;
                setDowngrade(null);
                performSwitch(target);
              }}
              className="h-11 bg-rose-600 font-semibold text-white hover:bg-rose-700 sm:order-3"
              data-testid="downgrade-confirm-button"
            >
              Switch anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
